import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Icon,
  Badge,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2, FiEye, FiEdit } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import { usePaywallContext } from '../contexts/PaywallContext';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { authService } from '../services/auth';
import { ClubLogo } from '../types/user';

const Advertising: React.FC = () => {
  const { user } = useAuth();
  const { userSubscription } = usePaywallContext();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentLogos, setCurrentLogos] = useState<ClubLogo[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Check if user is on a Club plan
  const isClubPlan = userSubscription?.planType === 'club';

  // Load existing logos on component mount
  React.useEffect(() => {
    if (user?.clubLogos) {
      setCurrentLogos(user.clubLogos);
    }
  }, [user?.clubLogos]);

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a JPG, PNG, or SVG file.';
    }

    // Check file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      return 'File size must be less than 2MB.';
    }

    return null;
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const error = validateFile(file);
    if (error) {
      toast({
        title: 'Invalid File',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Create preview from original file
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);
    onOpen();
  }, [toast, onOpen]);

  const handleUpload = async () => {
    if (!user || !fileInputRef.current?.files?.[0]) return;

    const file = fileInputRef.current.files[0];
    setIsUploading(true);

    try {
      // Check if user already has 3 logos
      if (currentLogos.length >= 3) {
        toast({
          title: 'Upload Failed',
          description: 'You can only upload up to 3 logos. Please delete one first.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Upload original file directly
      // Create storage reference
      const filename = `club-logo-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `club-logos/${user.uid}/${filename}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Get image dimensions from the original file
      const dimensions = await getImageDimensions(file);

      // Create logo object
      const logoData: ClubLogo = {
        url: downloadURL,
        filename: filename,
        uploadedAt: new Date(),
        size: file.size,
        dimensions: dimensions,
      };

      // Add new logo to existing array
      const updatedLogos = [...currentLogos, logoData];

      // Update user profile in Firestore
      await authService.updateUserProfile(user.uid, { clubLogos: updatedLogos });

      setCurrentLogos(updatedLogos);
      setPreviewUrl(null);
      onClose();

             toast({
         title: 'Uploaded successfully!',
         description: `(${updatedLogos.length}/3)`,
         status: 'success',
         duration: 5000,
         isClosable: true,
       });

    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload logo. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (logoToDelete: ClubLogo) => {
    if (!user) return;

    setIsDeleting(true);

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, `club-logos/${user.uid}/${logoToDelete.filename}`);
      
      await deleteObject(storageRef);

      // Remove logo from array
      const updatedLogos = currentLogos.filter(logo => logo.filename !== logoToDelete.filename);

      // Update user profile in Firestore
      await authService.updateUserProfile(user.uid, { clubLogos: updatedLogos });

      setCurrentLogos(updatedLogos);

    } catch (error: any) {
      console.error('Delete error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to delete logo. Please try again.';
      
      // Provide more specific error messages
      if (error.code === 'storage/object-not-found') {
        errorMessage = 'Logo file not found. It may have already been deleted.';
      } else if (error.code === 'storage/unauthorized') {
        errorMessage = 'Permission denied. Please check your authentication.';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Delete operation was canceled.';
      }
      
      toast({
        title: 'Delete Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isClubPlan) {
    return (
      <Box>
        <Alert status="info" bg="blue.900" borderColor="blue.700">
          <AlertIcon />
          <Box>
            <AlertTitle color="blue.200">Club Feature</AlertTitle>
            <AlertDescription color="blue.100">
              Logo upload is available for Club plan subscribers only. 
              Upgrade to a Club plan to add your logo to scoring screens.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">

                {/* Current Logos Display */}
        {currentLogos.length > 0 && (
          <Box>
                         <HStack justify="space-between" align="center" mb={3}>
               <Text fontWeight="semibold" color="white">
                 Uploads ({currentLogos.length}/3)
               </Text>
              {currentLogos.length < 3 && (
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiUpload} />}
                  onClick={() => fileInputRef.current?.click()}
                  colorScheme="blue"
                  variant="outline"
                >
                  Add Another
                </Button>
              )}
            </HStack>
            <VStack spacing={4} align="stretch">
              {currentLogos.map((logo, index) => (
                <Box
                  key={logo.filename}
                  p={4}
                  border="1px solid"
                  borderColor="gray.600"
                  borderRadius="md"
                  bg="gray.800"
                >
                  <HStack spacing={4} align="start">
                    <Image
                      src={logo.url}
                      alt={`Club Logo ${index + 1}`}
                      boxSize="100px"
                      objectFit="contain"
                      borderRadius="md"
                      border="1px"
                      borderColor="gray.200"
                    />
                                         <VStack align="start" spacing={2} flex={1}>
                      <Text fontSize="sm" color="gray.300">
                        Size: {formatFileSize(logo.size)}
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        Dimensions: {logo.dimensions.width} × {logo.dimensions.height}px
                      </Text>
                                             <HStack spacing={2}>
                         <Button
                           size="sm"
                           leftIcon={<Icon as={FiTrash2} />}
                           onClick={() => handleDelete(logo)}
                           colorScheme="red"
                           variant="outline"
                           isLoading={isDeleting}
                         >
                           Delete
                         </Button>
                       </HStack>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

                                                                       {/* Upload Section */}
           {currentLogos.length === 0 && (
             <Box>
               <Button
                 leftIcon={<Icon as={FiUpload} />}
                 onClick={() => fileInputRef.current?.click()}
                 colorScheme="blue"
                 variant="outline"
                 size="md"
               >
                 Upload
               </Button>
             </Box>
           )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/svg+xml"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Preview Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="md">
          <ModalOverlay />
          <ModalContent bg="#191919" color="white">
                         <ModalHeader>Preview</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {previewUrl && (
                <VStack spacing={4}>
                  <Image
                    src={previewUrl}
                    alt="Logo Preview"
                    maxH="300px"
                    objectFit="contain"
                    borderRadius="md"
                  />
                                     <Text fontSize="sm" color="gray.400">
                     This will be displayed on your scoring screens
                   </Text>
                </VStack>
              )}
            </ModalBody>
                         <ModalFooter>
               <Button variant="outline" colorScheme="gray" color="white" mr={3} onClick={onClose}>
                 Cancel
               </Button>
                             <Button
                 colorScheme="blue"
                 onClick={handleUpload}
                 isLoading={isUploading}
                 loadingText="Uploading..."
               >
                 Upload
               </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default Advertising;
